import React, { useEffect, useRef, useState } from 'react';
import {
    Autocomplete,
    Button,
    Dialog,
    FormLabel,
    TextField as MuiTextField,
    SearchIcon,
    Select,
} from '@biq/apex-ui';
import {
    Box,
    DialogContent,
    Grid,
    InputAdornment,
    MenuItem,
    Typography,
} from '@mui/material';
import {
    AddStepDefaults,
    AddStepFormData,
    addStepModalStyles,
    AddStepSchema,
} from './add-step-modal-helper';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useStep } from 'hooks/useSteps';
import { ExpandableItem, HelixLoader } from 'components';
import { ellipsifyTextInTwoLines, globalEllipsisTextStyles } from 'lib';
import { StepsType, StepTypeOption } from 'pages/pipelines/pipelines-helper';

interface AddStepModalProps {
    open: boolean;
    handleCloseButton: () => void;
    handleSubmitStepButton: (data: AddStepFormData) => void;
}

export const AddStepModal = ({
    open,
    handleCloseButton,
    handleSubmitStepButton,
}: AddStepModalProps): React.JSX.Element => {
    const {
        control,
        watch,
        setValue,
        handleSubmit,
        formState: { errors },
    } = useForm<AddStepFormData>({
        mode: 'all',
        resolver: yupResolver(AddStepSchema),
        defaultValues: AddStepDefaults,
    });
    const type = watch('type');
    const { data: stepData, isLoading } = useStep({ type });
    const descContainerRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState<number>();

    useEffect(() => {
        const updateDimensions = (): void => {
            if (descContainerRef.current) {
                const computedWidth =
                    descContainerRef.current.getBoundingClientRect().width -
                    116.26;
                setWidth(computedWidth);
            }
        };

        const observer = new ResizeObserver(() => {
            updateDimensions();
        });

        if (descContainerRef.current) {
            observer.observe(descContainerRef.current);
        }

        window.addEventListener('resize', updateDimensions);

        return () => {
            window.removeEventListener('resize', updateDimensions);
        };
    }, [descContainerRef.current]);

    const addStepSubmit = handleSubmit((data: AddStepFormData) => {
        handleSubmitStepButton(data);
    });

    return (
        <Dialog
            open={open}
            closable={true}
            onClose={handleCloseButton}
            title='Add Step'
            sx={addStepModalStyles.dialogStyles}
        >
            <DialogContent>
                <Grid sx={addStepModalStyles.mainContainer}>
                    <Box sx={addStepModalStyles.fieldGroup}>
                        <Controller
                            name='type'
                            control={control}
                            render={({ field }): React.JSX.Element => (
                                <Select
                                    {...field}
                                    label={'Type'}
                                    required={true}
                                    variant='outlined'
                                    onChange={(e): void => {
                                        field.onChange(e);
                                        setValue('step', null);
                                    }}
                                    error={!!errors.type}
                                    errorText={
                                        errors.type ? errors.type.message : ''
                                    }
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                ...addStepModalStyles.typeOptionsList,
                                            },
                                        },
                                    }}
                                >
                                    {StepsType.map((item: StepTypeOption) => (
                                        <MenuItem
                                            key={item.type}
                                            value={item.type}
                                        >
                                            {item.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            )}
                        />
                    </Box>
                    <Box
                        sx={addStepModalStyles.fieldGroup}
                        ref={descContainerRef}
                    >
                        <FormLabel
                            sx={addStepModalStyles.stepsLabelStyles}
                            label='Steps'
                            required
                        />
                        <Controller
                            name='step'
                            control={control}
                            render={({ field }): React.JSX.Element => (
                                <Autocomplete
                                    {...field}
                                    value={field.value}
                                    disableClearable={!field.value}
                                    options={type ? stepData?.data ?? [] : []}
                                    onChange={(_, value): void =>
                                        field.onChange(value)
                                    }
                                    getOptionLabel={(option): string =>
                                        option?.name ?? ''
                                    }
                                    noOptionsText={
                                        type ? (
                                            <Box
                                                sx={
                                                    addStepModalStyles.noOptionStateStyles
                                                }
                                            >
                                                {isLoading ? (
                                                    <HelixLoader />
                                                ) : (
                                                    <>
                                                        <Typography
                                                            sx={
                                                                addStepModalStyles.noOptionStateHeading
                                                            }
                                                        >
                                                            No Steps Found
                                                        </Typography>
                                                        <Typography variant='body1'>
                                                            Try searching by
                                                            another name.
                                                        </Typography>
                                                    </>
                                                )}
                                            </Box>
                                        ) : (
                                            'No options'
                                        )
                                    }
                                    sx={addStepModalStyles.stepsSelect}
                                    ListboxProps={{
                                        style: {
                                            ...(addStepModalStyles.stepOptionListConatiner as React.CSSProperties),
                                        },
                                    }}
                                    renderOption={(
                                        props,
                                        option
                                    ): React.JSX.Element => (
                                        <li
                                            {...props}
                                            style={
                                                addStepModalStyles.stepOptionList as React.CSSProperties
                                            }
                                        >
                                            <Typography
                                                variant='body1'
                                                sx={{
                                                    ...globalEllipsisTextStyles,
                                                    ...ellipsifyTextInTwoLines,
                                                }}
                                            >
                                                {option?.name}
                                            </Typography>
                                            <Box
                                                sx={
                                                    addStepModalStyles.optionDescriptionConatiner
                                                }
                                            >
                                                <Typography
                                                    variant='body1'
                                                    sx={
                                                        addStepModalStyles.descriptionHeadingStyles
                                                    }
                                                >
                                                    Description:
                                                </Typography>
                                                <ExpandableItem
                                                    textMessage={
                                                        option?.description
                                                    }
                                                    customStyles={
                                                        addStepModalStyles.menuItemDescriptionStyles
                                                    }
                                                    textMessageWidth={width}
                                                />
                                            </Box>
                                        </li>
                                    )}
                                    renderInput={(
                                        params
                                    ): React.JSX.Element => (
                                        <MuiTextField
                                            {...params}
                                            size='medium'
                                            variant='outlined'
                                            required
                                            error={!!errors.step}
                                            errorText={
                                                errors.step?.message ?? ''
                                            }
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: [
                                                    <InputAdornment
                                                        key='searchIcon'
                                                        position='start'
                                                    >
                                                        <SearchIcon />
                                                    </InputAdornment>,
                                                ],
                                            }}
                                        />
                                    )}
                                />
                            )}
                        />
                    </Box>
                </Grid>
                <Grid sx={addStepModalStyles.buttonContainer}>
                    <Button variant='text' onClick={handleCloseButton}>
                        Cancel
                    </Button>
                    <Button
                        color='primary'
                        variant='contained'
                        onClick={addStepSubmit}
                        sx={addStepModalStyles.addStepButton}
                    >
                        Add Step
                    </Button>
                </Grid>
            </DialogContent>
        </Dialog>
    );
};
